import React, { useEffect } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import LumoChat from "./components/LumoChat";
import MicroLessonBubble from "./components/MicroLessonBubble";
import { useTenant } from "./contexts/TenantContext";

// Conditional chat component - Lumo for coaching, Nova for UGC
const ConditionalChat = () => {
  const location = useLocation();
  const path = location.pathname;
  const isUGCPage = path.includes('/app/ugc') || path.includes('/teleprompter');
  const allowedPrefixes = [
    '/app/dashboard',
    '/app/daily-drill',
    '/app/objection-dojo',
    '/app/mega-trainer',
    '/app/meet-lumo',
    '/app/conversation-analyzer',
    '/app/ai-roleplay',
    '/app/technique-practice',
    '/app/lumo-progress',
    '/app/micro-lessons',
    '/app/techniques-library',
    '/app/cascade-challenge',
    '/app/leaderboards'
  ];

  const isCoachingPage = allowedPrefixes.some(prefix => path.startsWith(prefix));
  if (!isCoachingPage || isUGCPage) return null;
  return <LumoChat />;
};

// Authentication
import Authentication from './pages/authentication';
import UGCAuthentication from './pages/ugc-authentication';
import WhopCallback from './pages/whop-callback';
import WhopSetup from './pages/whop-setup';
import PreCheckout from './pages/pre-checkout';
import ClaimAccount from './pages/claim-account';
import ResetPassword from './pages/reset-password';
import UpdatePassword from './pages/update-password';

// Dashboard & Home
import HomeDashboard from './pages/home-dashboard';

// User Settings
import Settings from './pages/settings';

// Drills & Practice
import DrillsManagement from './pages/drills-management';
import DailyDrill from './pages/daily-drill';
import ObjectionDojo from './pages/objection-dojo';

// Learning & Coaching
import MeetLumo from './pages/meet-lumo';
import ConceptsPractice from './pages/concepts-practice';
import MicroLessons from "./pages/micro-lessons";
import Leaderboards from './pages/leaderboards';
import MegaTrainer from './pages/mega-trainer';
import LessonViewer from './pages/mega-trainer/LessonViewer';
import ScenarioPractice from './pages/mega-trainer/ScenarioPractice';

// Tools & Resources
import ScriptLab from './pages/script-lab';
import UploadCenter from './pages/upload-center';
import IndustrySelection from './pages/industry-selection';

// Practice & Coaching Tools
import ConversationAnalyzer from './pages/conversation-analyzer';
import AIRolePlay from './pages/ai-roleplay';
import TrainingPage from './pages/training';
import TechniquePractice from './pages/technique-practice';

// Gamification
import GamificationDashboard from './pages/gamification';
import PrestigeDrillsPage from './pages/prestige-drills';

// Lumo Progress
import LumoProgress from './pages/lumo-progress';
import TechniquesLibrary from './pages/techniques-library';
import CascadeChallenge from './pages/cascade-challenge';

// Activity & Training
import ActivityReportPage from './pages/activity-report';

// Admin Tools
import EmbeddingsAdmin from './pages/embeddings-admin';
import AdminPage from './pages/admin';

// Subscription
import SubscriptionPage from './pages/subscription';
import UGCLanding from './pages/ugc';
import UGCPricing from './pages/ugc-subscription';
import UGConboarding from './pages/ugc-onboarding';

// UGC Components (with NovaChatProvider)
import UGCWorkspaceWithProvider from './pages/ugc-scripts';
import UGCDashboardWithProvider from './pages/ugc-dashboard';
import TeleprompterWithProvider from './pages/teleprompter';
import UGCHubWithProvider from './pages/ugc-hub';
import UGCBrandsPageWithProvider from './pages/ugc-brands';
import UGCCommunityPageWithProvider from './pages/ugc-community';
import UGCCRMPageWithProvider from './pages/ugc-crm';
import UGCMarketplace from './pages/ugc-marketplace';
import UGCMedia from './pages/ugc-media';
import UGCSettings from './pages/ugc-settings';
import BrandDashboard from './pages/ugc-brand-dashboard';
import CreatorInvoices from './pages/ugc-invoices';
import BrandInvoices from './pages/ugc-invoices-brand';
import GmailCallback from './pages/ugc-crm/gmail-callback';
import WhopUGCSuccess from './pages/whop-ugc-success';
import { useAuth } from './contexts/AuthContext';
import { useSubscription } from './hooks/useSubscription';
import { hasFeatureAccess } from './config/featureAccess';

// Landing Page
import LandingPage from './pages/LandingPage';
// SEO Pages
import SalesTraining from './pages/seo/SalesTraining';
import AISalesCoach from './pages/seo/AISalesCoach';
import ObjectionHandling from './pages/seo/ObjectionHandling';
import SalesFrameworks from './pages/seo/SalesFrameworks';
import RoleplayScenarios from './pages/seo/RoleplayScenarios';
import Guides from './pages/seo/Guides';

// Legal Pages
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
// Affiliate
import AffiliatePage from './pages/affiliate';

// Whop Success
import WhopSuccess from './pages/WhopSuccess';

// Email Verified
import EmailVerified from './pages/email-verified';

// Handle recovery links that land on the root domain (Supabase sometimes drops the path)
// This is critical for password reset emails - they contain recovery tokens in the URL hash
const RecoveryRedirector = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const { hash, pathname } = window.location;
    
    // Only run this check once on mount
    if (!hash) return;
    
    console.log('🔍 RecoveryRedirector:', { pathname, hash: hash.substring(0, 50) + '...' });
    
    // If this is a recovery/reset password flow
    if (hash.includes('type=recovery')) {
      // If we're not already on the update-password page, navigate there
      if (pathname !== '/update-password') {
        console.log('↗️  Redirecting to /update-password to process recovery token');
        // Use window.location to preserve the hash fragment exactly
        window.location.href = '/update-password' + hash;
      }
      // If we're already on /update-password, the component will handle the hash
    }
  }, [navigate]);

  return null;
};

// Basic protected route for authenticated users (non-UGC specific)
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  const { tenantSlug } = useTenant();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      const tenantQuery = tenantSlug && tenantSlug !== 'default' ? `&tenant=${encodeURIComponent(tenantSlug)}` : '';
      navigate(`/authentication?mode=login${tenantQuery}`, { replace: true });
    }
  }, [user, navigate, tenantSlug]);

  if (!user) return null;
  return children;
};

const ProtectedUGC = ({ children, onboarding = false }) => {
  const { user, profile } = useAuth();
  const { ugcTier } = useSubscription();
  const navigate = useNavigate();
  const userTier = ugcTier || profile?.ugc_tier || 'free';
  const requiredPath = onboarding ? 'app/ugc-onboarding' : 'app/ugc-dashboard';
  const hasAccess = hasFeatureAccess(requiredPath, userTier);
  const introDone = Boolean(
    profile?.ugc_onboarding_completed ||
    (typeof window !== 'undefined' && localStorage.getItem('ugc_intro_done') === 'true')
  );
  const [isReady, setIsReady] = React.useState(false);

  useEffect(() => {
    // Reset ready state when user/profile changes
    setIsReady(false);

    if (!user) {
      navigate('/ugc-authentication?mode=login', { replace: true });
      return;
    }

    // Wait for profile to load before deciding access
    if (!profile) {
      return;
    }

    if (!hasAccess) {
      navigate('/ugc-subscription', { replace: true });
      return;
    }

    if (!introDone && !onboarding) {
      navigate('/app/ugc-onboarding', { replace: true });
      return;
    }

    // All checks passed, mark as ready
    setIsReady(true);
  }, [user, profile, hasAccess, introDone, onboarding, navigate]);

  if (!user) return <div className="p-6 text-center text-muted-foreground">Redirecting to login...</div>;
  if (!profile) return <div className="p-6 text-center text-muted-foreground">Loading your profile...</div>;
  if (!hasAccess) return <div className="p-6 text-center text-muted-foreground">Redirecting to UGC plans...</div>;
  if (!introDone && !onboarding) return <div className="p-6 text-center text-muted-foreground">Redirecting to onboarding...</div>;
  if (!isReady) return <div className="p-6 text-center text-muted-foreground">Preparing your workspace...</div>;
  return children;
};

export default function Routes() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RecoveryRedirector />
        <RouterRoutes>
          {/* Landing Page */}
          <Route path="/" element={<LandingPage />} />
          {/* SEO Cornerstone Pages */}
          <Route path="/sales-training" element={<SalesTraining />} />
          <Route path="/ai-sales-coach" element={<AISalesCoach />} />
          <Route path="/objection-handling-training" element={<ObjectionHandling />} />
          <Route path="/sales-frameworks" element={<SalesFrameworks />} />
          <Route path="/sales-roleplay-scenarios" element={<RoleplayScenarios />} />
          <Route path="/sales-guides" element={<Guides />} />
          
          {/* Legal Pages */}
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          {/* Affiliate */}
          <Route path="/affiliate" element={<AffiliatePage />} />
          
          {/* Authentication */}
          <Route path="/authentication" element={<Authentication />} />
          <Route path="/ugc-authentication" element={<UGCAuthentication />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/whop-callback" element={<WhopCallback />} />
          <Route path="/whop-setup" element={<WhopSetup />} />
          <Route path="/whop-success" element={<WhopSuccess />} />
          <Route path="/whop-ugc-success" element={<WhopUGCSuccess />} />
          <Route path="/pre-checkout" element={<PreCheckout />} />
          <Route path="/claim-account" element={<ClaimAccount />} />
          <Route path="/email-verified" element={<EmailVerified />} />
          
          {/* Authenticated App Routes */}
          <Route path="/app/dashboard" element={<HomeDashboard />} />
          <Route path="/app/settings" element={<Settings />} />
          <Route path="/app/industry-selection" element={<IndustrySelection />} />
          
          {/* Drills & Practice */}
          <Route path="/app/drills-management" element={<DrillsManagement />} />
          <Route path="/app/daily-drill" element={<DailyDrill />} />
          <Route path="/app/objection-dojo" element={<ObjectionDojo />} />
          <Route path="/app/mega-trainer" element={<MegaTrainer />} />
          <Route path="/app/trainer/:moduleId/lesson/:lessonNumber" element={<LessonViewer />} />
          <Route path="/app/trainer/:moduleId/scenarios" element={<ScenarioPractice />} />
          
          {/* Learning & Coaching */}
          <Route path="/app/meet-lumo" element={<MeetLumo />} />
          <Route path="/app/coaching-concepts" element={<Navigate to="/app/techniques-library" replace />} />
          <Route path="/app/concepts-practice/:id" element={<ConceptsPractice />} />
          <Route path="/app/micro-lessons" element={<MicroLessons />} />
          <Route path="/app/leaderboards" element={<Leaderboards />} />
          
          {/* Tools & Resources */}
          <Route path="/app/script-lab" element={<ScriptLab />} />
          <Route path="/app/upload-center" element={<UploadCenter />} />
          
          {/* Practice & Coaching Tools */}
          <Route path="/app/conversation-analyzer" element={<ConversationAnalyzer />} />
          <Route path="/app/ai-roleplay" element={<AIRolePlay />} />
          <Route path="/app/ai-roleplay/:sessionId" element={<AIRolePlay />} />
          <Route path="/app/technique-practice/:slug" element={<TechniquePractice />} />
          <Route path="/app/training/:courseId" element={<TrainingPage />} />
          
          {/* Achievements */}
          <Route path="/app/gamification" element={<GamificationDashboard />} />
          <Route path="/app/prestige-drills" element={<PrestigeDrillsPage />} />
          
          {/* Lumo Progress */}
          <Route path="/app/lumo-progress" element={<LumoProgress />} />
          <Route path="/app/techniques-library" element={<TechniquesLibrary />} />
          <Route path="/app/cascade-challenge" element={<CascadeChallenge />} />
          
          {/* Activity Report */}
          <Route path="/app/activity-report" element={<ActivityReportPage />} />
          
          {/* Admin Tools */}
          <Route path="/app/embeddings-admin" element={<EmbeddingsAdmin />} />
          <Route path="/app/admin" element={<AdminPage />} />
          
          {/* Subscription */}
          <Route path="/app/subscription" element={<SubscriptionPage />} />
          <Route path="/app/teleprompter" element={<ProtectedUGC><TeleprompterWithProvider /></ProtectedUGC>} />
          <Route path="/app/ugc-marketplace" element={<ProtectedUGC><UGCMarketplace /></ProtectedUGC>} />
          <Route path="/app/ugc-dashboard" element={<ProtectedUGC><UGCDashboardWithProvider /></ProtectedUGC>} />
          <Route path="/app/ugc-scripts" element={<ProtectedUGC><UGCWorkspaceWithProvider /></ProtectedUGC>} />
          <Route path="/app/ugc-onboarding" element={<ProtectedUGC onboarding><UGConboarding /></ProtectedUGC>} />
          <Route path="/app/ugc-hub" element={<ProtectedUGC><UGCHubWithProvider /></ProtectedUGC>} />
          <Route path="/app/ugc-brands" element={<ProtectedUGC><UGCBrandsPageWithProvider /></ProtectedUGC>} />
          <Route path="/app/ugc-community" element={<ProtectedUGC><UGCCommunityPageWithProvider /></ProtectedUGC>} />
          <Route path="/app/ugc-crm" element={<ProtectedUGC><UGCCRMPageWithProvider /></ProtectedUGC>} />
          <Route path="/app/ugc-crm/gmail-callback" element={<ProtectedUGC><GmailCallback /></ProtectedUGC>} />
          <Route path="/app/ugc-media" element={<ProtectedUGC><UGCMedia /></ProtectedUGC>} />
          <Route path="/app/ugc-settings" element={<ProtectedUGC><UGCSettings /></ProtectedUGC>} />
          <Route path="/app/ugc-brand-dashboard" element={<ProtectedUGC><BrandDashboard /></ProtectedUGC>} />
          <Route path="/app/ugc-invoices" element={<ProtectedUGC><CreatorInvoices /></ProtectedUGC>} />
          <Route path="/app/ugc-invoices-brand" element={<ProtectedUGC><BrandInvoices /></ProtectedUGC>} />
          <Route path="/ugc-subscription" element={<UGCPricing />} />
          <Route path="/ugc" element={<UGCLanding />} />
          
          {/* Legacy redirects for old routes */}
          <Route path="/home-dashboard" element={<HomeDashboard />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/daily-drill" element={<DailyDrill />} />
          <Route path="/objection-dojo" element={<ObjectionDojo />} />
          
          {/* 404 Fallback */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
      <ConditionalChat />
      <MicroLessonBubble />
    </BrowserRouter>
  );
}


